import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";

/**
 * Hook to access current tier + usage. Polls every 60s.
 * Returns: { tier, isPremium, limits, usage, refresh }
 */
// Last known tier, shared by every mount: each page renders its own
// AppLayout, and starting from the "free" default made the plan badge and
// the beta banner jump on every navigation.
let cachedTier = null;

const DEFAULT_TIER = {
  tier: "free",
  tierLabel: "Free",
  isPremium: false,
  premiumSource: null,
  subscriptionStatus: null,
  betaMode: false,
  trialActive: false,
  trialEndsAt: null,
  capabilities: { ai_copilot: false, lost_opportunity: false, advanced_analytics: false, api_export: false, priority_support: false, multi_user: false },
  limits: { quotes_per_month: 10, ai_insights_per_day: 1, marketplace_write: false },
  usage: { quotes_this_month: 0, ai_insights_today: 0 },
  loading: true,
};

export default function useTier() {
  const [state, setState] = useState(() => cachedTier || DEFAULT_TIER);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/me/tier");
      cachedTier = {
        tier: data.tier,
        tierLabel: data.tier_label,
        assignedTierLabel: data.assigned_tier_label || null,
        isPremium: data.is_premium,
        premiumSource: data.premium_source || null,
        subscriptionStatus: data.subscription_status,
        betaMode: !!data.beta_mode,
        trialActive: !!data.trial_active,
        trialEndsAt: data.trial_ends_at || null,
        capabilities: data.capabilities || {},
        limits: data.limits,
        usage: data.usage,
        loading: false,
      };
      setState(cachedTier);
    } catch (_e) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  return { ...state, refresh };
}
